import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import {
  People,
  Phone,
  Speed,
  Assignment,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TabPanel,
  StyledTabs,
  KPICard,
  SectionHeader,
  ChartCard,
  DataTable,
  RankBadge,
  COLORS,
  CHART_COLORS,
  PageContainer,
} from './components/ReportShared';
import DateRangeFilter, { getDefaultDateRange } from './components/DateRangeFilter';
import api from '../../services/api';

const TAB_LABELS = [
  'Performance Overview',
  'Rankings & Leaderboard',
];

function AgentIntelligence() {
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());

  const [kpis, setKpis] = useState({
    totalAgents: 0,
    dialledCallsPerDay: 0,
    connectedCallsPerDay: 0,
    connectedLeadsPerDay: 0,
    avgConversionRate: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loadingPerf, setLoadingPerf] = useState(false);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  const handleTabChange = (e, v) => setTabValue(v);
  const currentMonth = new Date().toLocaleString('en-US', { month: 'long' });

  const buildDatePayload = () => ({
    date_range: {
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    },
  });

  const buildCurrentMonthPayload = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    return {
      date_range: {
        start_date: formatDate(start),
        end_date: formatDate(now),
      },
    };
  };

  const fetchPerformance = async () => {
    try {
      setLoadingPerf(true);
      const response = await api.post('/reportv2/agent-intelligence/performance/', buildDatePayload());
      const data = response.data || {};
      setKpis(data.kpis || {});
      const table = data.table || [];
      const connectedLeadsByAgent = new Map(
        table.map((row) => [row.agent, row.leadsConnected ?? 0]),
      );
      const chart = (data.chart || []).map((row) => ({
        ...row,
        connectedLeads:
          row.connectedLeads
          ?? row.leadsConnected
          ?? connectedLeadsByAgent.get(row.agent)
          ?? 0,
      }));
      setChartData(chart);
      setTableData(table);
    } catch (err) {
      console.error('Error fetching agent intelligence performance:', err);
    } finally {
      setLoadingPerf(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      setLoadingLeaderboard(true);
      const response = await api.post('/reportv2/agent-intelligence/leaderboard/', buildCurrentMonthPayload());
      const data = response.data || {};
      setLeaderboardData(data.leaderboard || []);
    } catch (err) {
      console.error('Error fetching agent intelligence leaderboard:', err);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  useEffect(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return;
    fetchPerformance();
    fetchLeaderboard();
  }, [dateRange]);

  const performanceTableColumns = [
    { label: 'Agent', field: 'agent' },
    { label: 'Active Leads', field: 'activeLeads', align: 'right' },
    { label: 'Leads Assigned', field: 'leadsAssigned', align: 'right' },
    { label: 'Untouched Leads', field: 'untouchedLeads', align: 'right' },
    { label: 'Total Calls', field: 'calls', align: 'right' },
    {
      label: 'Outgoing',
      field: 'outgoing',
      align: 'right',
    },
    {
      label: 'Incoming',
      field: 'incoming',
      align: 'right',
    },
    {
      label: 'Connected',
      field: 'connected',
      align: 'right',
    },
    { label: 'Leads Connected', field: 'leadsConnected', align: 'right' },
    { label: 'Talktime (Overall)', field: 'talktimeOverall', align: 'right' },
    { label: 'Talktime(Leads)', field: 'talktimeLeads', align: 'right' },
    { label: 'Details Shared', field: 'detailsShared', align: 'right' },
    { label: 'Site Visits', field: 'siteVisits', align: 'right' },
    { label: 'Bookings', field: 'bookings', align: 'right' },
    { label: 'Conversion %', field: 'conversion', align: 'right', render: (v) => `${v}%` },
  ];

  const exportPerformanceTableCsv = () => {
    const csvEscape = (val) => {
      const str = val == null ? '' : String(val);
      if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };

    const headers = performanceTableColumns.map((c) => c.label);
    const csvRows = tableData.map((row) =>
      performanceTableColumns.map((col) => {
        const rawValue = row[col.field];
        return col.render ? col.render(rawValue, row) : rawValue;
      }),
    );

    const csv = [headers, ...csvRows].map((r) => r.map(csvEscape).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'agent_performance_table.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <PageContainer>
      <StyledTabs value={tabValue} onChange={handleTabChange} tabs={TAB_LABELS} />

      {/* Tab 1: Performance Overview */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Performance Overview
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Agent performance for the selected period
            </Typography>
          </Box>
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </Box>

        {loadingPerf ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(5, 1fr)',
                  },
                  gap: 3,
                }}
              >
                <KPICard title="Total Agents" value={String(kpis.totalAgents ?? 0)} icon={People} />
                <KPICard title="Dialled Calls/Day" value={String(kpis.dialledCallsPerDay ?? 0)} icon={Phone} />
                <KPICard title="Connected Calls/Day" value={String(kpis.connectedCallsPerDay ?? 0)} icon={Phone} />
                <KPICard title="Connected Leads/Day" value={String(kpis.connectedLeadsPerDay ?? 0)} icon={People} />
                <KPICard title="Avg Conversion Rate" value={`${kpis.avgConversionRate ?? 0}%`} icon={Speed} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ChartCard title="Agent-wise Performance" height={320}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis dataKey="agent" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="calls" fill={CHART_COLORS[0]} name="Calls" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="connectedLeads" fill={CHART_COLORS[1]} name="Connected Leads" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="detailsShared" fill={CHART_COLORS[2]} name="Details Shared" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="siteVisits" fill={CHART_COLORS[3]} name="Site Visits" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="bookings" fill={CHART_COLORS[4]} name="Bookings" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0 }}>
                  Performance Table
                </Typography>
                <Button variant="outlined" size="small" onClick={exportPerformanceTableCsv}>
                  Export
                </Button>
              </Box>
              <DataTable
                columns={performanceTableColumns}
                rows={tableData}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Tab 2: Rankings & Leaderboard */}
      <TabPanel value={tabValue} index={1}>
        {loadingLeaderboard ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <SectionHeader title="Agent Leaderboard This Month" subtitle={`Top performers by composite score - ${currentMonth}`} />
              <DataTable
                columns={[
                  { label: 'Rank', field: 'rank', align: 'center', render: (v) => <RankBadge rank={v} /> },
                  { label: 'Agent', field: 'agent' },
                  { label: 'Score', field: 'score', align: 'right' },
                  { label: 'Calls', field: 'calls', align: 'right' },
                  { label: 'Leads Connected', field: 'leadsConnected', align: 'right' },
                  { label: 'Total Talktime', field: 'totalTalktime', align: 'right' },
                  { label: 'Avg Call Duration', field: 'avgCallDuration', align: 'right' },
                  { label: 'Site Visits', field: 'siteVisits', align: 'right' },
                  { label: 'Conversions', field: 'conversions', align: 'right' },
                  { label: 'Revenue', field: 'revenue', align: 'right' },
                ]}
                rows={leaderboardData}
              />
            </Grid>
          </Grid>
        )}
      </TabPanel>
    </PageContainer>
  );
}

export default AgentIntelligence;
